import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ArrowTopRightOnSquareIcon, MinusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import SolicitacaoDetailsContent from '../../SolicitacaoDetailsPage/SolicitacaoDetailsContent';

interface SolicitacaoDrawerProps {
  solicitacaoId: string | null;
  /** Ver SolicitacaoDetailsContent.movimentacaoInicialId. */
  movimentacaoId?: string | null;
  /** Ver SolicitacaoDetailsContent — força visão de solicitante mesmo pra SI, quando
   * aberto pelas abas "Que eu abri"/"Minha Equipe" em vez de "Controle de Solicitações". */
  forcarVisaoSolicitante?: boolean;
  /** EXPERIMENTO — ver SolicitacoesKanban.handleDrop. */
  destacarPendencia?: boolean;
  onClose: () => void;
  /** Mostra "Minimizar" — quem usa guarda a solicitação na pilha de minimizadas
   * (SolicitacoesMinimizadas) e fecha a janela. Sem isso (ex.: solicitação relacionada
   * aberta de dentro de outra), o botão não aparece. */
  onMinimizar?: () => void;
}

/**
 * Detalhe da Solicitação aberto direto da lista: janela larga (quase a tela toda — a lateral
 * de Movimentações + a selecionada + a gaveta cabem juntas), sem título próprio porque
 * `SolicitacaoDetailsContent` já tem cabeçalho. "Abrir em nova aba" leva pra página própria
 * (`/si/solicitacoes/:id`); "Minimizar" fica a cargo de quem usa (ver `onMinimizar`).
 * Arrastar não faz sentido numa janela desse tamanho (decisão do SI).
 *
 * Mesmo nome de arquivo/props do Drawer original de propósito — SolicitacoesListPage e
 * SolicitacaoRelacionadaInfo não precisam mudar.
 */
export default function SolicitacaoDrawer({
  solicitacaoId,
  movimentacaoId,
  forcarVisaoSolicitante,
  destacarPendencia,
  onClose,
  onMinimizar,
}: SolicitacaoDrawerProps) {
  return (
    <Transition show={solicitacaoId != null} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-2 sm:p-4">
          <Transition.Child
            as={Fragment}
            enter="transition ease-out duration-200 transform"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="transition ease-in duration-150 transform"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="relative flex h-[94vh] w-full max-w-[1400px] flex-col overflow-hidden rounded-lg bg-gray-50 shadow-xl">
              <div className="flex flex-shrink-0 items-center justify-end gap-1 border-b border-gray-200 bg-white px-3 py-2">
                {solicitacaoId && (
                  <a
                    href={`/si/solicitacoes/${solicitacaoId}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex h-10 items-center gap-1.5 rounded-md px-3 text-sm text-gray-600 hover:bg-gray-100"
                  >
                    <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
                    Abrir em nova aba
                  </a>
                )}
                {onMinimizar && (
                  <button
                    type="button"
                    className="inline-flex h-10 items-center gap-1.5 rounded-md px-3 text-sm text-gray-600 hover:bg-gray-100"
                    onClick={onMinimizar}
                  >
                    <MinusIcon className="h-4 w-4" aria-hidden="true" />
                    Minimizar
                  </button>
                )}
                <button
                  type="button"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100"
                  onClick={onClose}
                >
                  <span className="sr-only">Fechar</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <div className="flex-grow overflow-y-auto">
                {solicitacaoId && (
                  <SolicitacaoDetailsContent
                    key={`${solicitacaoId}:${movimentacaoId ?? ''}`}
                    id={solicitacaoId}
                    movimentacaoInicialId={movimentacaoId}
                    forcarVisaoSolicitante={forcarVisaoSolicitante}
                    destacarPendencia={destacarPendencia}
                  />
                )}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
